/*
 * Copyright (c) 2024. Bolmate B.V.
 */
import './style/customerDetails.scss';

import {
    AwesomeIcon, ChangeFunction, ChangeKeyFunction, CloseButton, Loader, preventDefault, WithBackground
} from '@dvrd/dvr-controls';
import React, {MouseEventHandler, useContext, useMemo} from 'react';
import {StripeAddress, StripeCustomer, StripeTaxID, StripeVerificationStatus} from "../../../../util/interfaces";
import {BolmateButton, BolmateInput, BolmateSelect} from "../../../controls";
import {AppContext} from "../../../misc/appContext";
import {COUNTRIES} from "../../../../util/countryUtil";
import {IconName} from '@fortawesome/fontawesome-svg-core';
import classNames from 'classnames';
import InfoMessage from "../../../misc/messages/infoMessage";

interface Props {
    onClose: MouseEventHandler;
    onChangeCustomer: ChangeKeyFunction<keyof StripeCustomer, string>;
    onChangeCustomerAddress: ChangeKeyFunction<keyof StripeAddress, string>;
    onChangeTaxID: ChangeFunction<string>;
    onAddTaxID: MouseEventHandler;
    onDeleteTaxID: (taxID: StripeTaxID) => MouseEventHandler;
    onSubmitCustomer: MouseEventHandler;
    customer: StripeCustomer;
    taxIDs: Array<StripeTaxID>;
    loading: boolean;
    taxIDValue: string;
    active: boolean;
    hasChanges: boolean;
}

export default function CustomerDetails(props: Props) {
    const {account} = useContext(AppContext).accountContext;
    const {
        onClose, onChangeCustomer, onChangeCustomerAddress, onChangeTaxID, onAddTaxID, onDeleteTaxID, onSubmitCustomer,
        customer, taxIDs, loading, taxIDValue, active, hasChanges
    } = props;
    const countries = useMemo(() => {
        return Object.entries(COUNTRIES)
            .map(([countryCode, countryLabel]) => ({value: countryCode, label: countryLabel}));
    }, []);

    function renderBody() {
        const {address} = customer;
        return (
            <div className='body-container'>
                <Loader active={loading}/>
                <InfoMessage className='disclaimer'>Let op, deze gegevens worden enkel gebruikt voor de facturen die je
                    van ons ontvangt. Deze gegevens worden niet gebruikt op de facturen die we naar jouw klanten
                    sturen.</InfoMessage>
                <form className='customer-container' onSubmit={preventDefault}>
                    <BolmateInput onChange={onChangeCustomer('name')} value={customer.name ?? ''}
                                  label='Bedrijfsnaam' placeholder={account?.companyName ?? 'Bedrijfsnaam'}/>
                    <BolmateInput onChange={onChangeCustomer('email')} value={customer.email ?? ''}
                                  label='E-mail voor facturatie' placeholder={account?.email ?? 'E-mailadres'}/>
                    <div className='row street-row'>
                        <BolmateInput onChange={onChangeCustomerAddress('line1')} value={address.line1 ?? ''}
                                      label='Straat + nummer' placeholder={account?.address ?? 'Adres'}/>
                        <BolmateInput onChange={onChangeCustomerAddress('line2')} value={address.line2 ?? ''}
                                      label='Toevoeging' placeholder='Toevoeging'/>
                    </div>
                    <div className='row'>
                        <BolmateInput onChange={onChangeCustomerAddress('postal_code')}
                                      value={address.postal_code ?? ''}
                                      label='Postcode' placeholder={account?.zipCode ?? 'Postcode'}/>
                        <BolmateInput onChange={onChangeCustomerAddress('city')} value={address.city ?? ''}
                                      label='Stad' placeholder={account?.city ?? 'Stad'}/>
                    </div>
                    <BolmateSelect value={customer.address.country ?? 'NL'}
                                   onChange={onChangeCustomerAddress('country')}
                                   items={countries} label='Land' itemsHeight='20rem' itemsPosition='top'/>
                </form>
                {renderTaxIDs()}
            </div>
        )
    }

    function renderTaxIDs() {
        return (
            <section className='tax-ids-container'>
                <label className='section-title'>BTW-nummers</label>
                <div className='tax-ids'>
                    {taxIDs.map(renderTaxID)}
                </div>
                <div className='add-row'>
                    <BolmateInput onChange={onChangeTaxID} value={taxIDValue} label='Nieuw BTW-nummer toevoegen'
                                  placeholder='BTW-nummer'/>
                    <BolmateButton onClick={onAddTaxID} label='+ Toevoegen' secondary/>
                </div>
            </section>
        )
    }

    function renderTaxID(taxId: StripeTaxID, index: number) {
        let verificationIcon: IconName = 'clock';
        if (taxId.verification?.status === StripeVerificationStatus.VERIFIED) verificationIcon = 'circle-check';
        else if (taxId.verification?.status !== StripeVerificationStatus.PENDING) verificationIcon = 'circle-xmark';
        return (
            <div className='tax-id' key={index}>
                {!!taxId.verification && <div className='verification-info'>
                    <AwesomeIcon name={verificationIcon} prefix='far'
                                 className={classNames('verification-icon', taxId.verification?.status)}/>
                    <p className='verification-message'>
                        {taxId.verification.status === StripeVerificationStatus.VERIFIED && (
                            <>
                                BTW-nummer gevalideerd<br/><br/>
                                {taxId.verification.verified_name}<br/>
                                {taxId.verification.verified_address}
                            </>
                        )}
                        {taxId.verification.status === StripeVerificationStatus.PENDING && (
                            <>BTW-nummer wordt gevalideerd</>
                        )}
                        {taxId.verification.status === StripeVerificationStatus.UNVERIFIED && (
                            <>Het BTW-nummer is niet geldig</>
                        )}
                        {taxId.verification.status === StripeVerificationStatus.UNAVAILABLE && (
                            <>Het BTW-nummer kan niet gevalideerd worden</>
                        )}
                    </p>
                </div>}
                <label className='tax-id-label'>{taxId.value}</label>
                <AwesomeIcon name='trash-alt' className='btn-delete' onClick={onDeleteTaxID(taxId)}/>
            </div>
        )
    }

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='default-popup customer-details'>
                <div className='header-container'>
                    <label className='header-title'>Factuurgegevens</label>
                    <CloseButton onClick={onClose}/>
                </div>
                {renderBody()}
                <div className='footer-container btn-2'>
                    <BolmateButton onClick={onClose} label='Sluiten' disabled={loading} secondary/>
                    <BolmateButton onClick={onSubmitCustomer} label='Opslaan' disabled={loading || !hasChanges}/>
                </div>
            </div>
        </WithBackground>
    )
}